import { ApiError } from './client.ts';
import type { MessageKey } from '../i18n/messages.en.ts';
import type { TFunction } from '../i18n/translate.ts';

/**
 * MVP-344: the one shape for turning a caught value into copy an operator can read.
 *
 * `String(err)` (or `${err}`) on a caught value produces the literal `[object Object]` for anything
 * that is not an `Error`, and `Error: <message>` — class name included, always in English — for
 * anything that is. Both used to reach the KJ as user-facing text in nine places.
 *
 * MVP-371 corrects what MVP-344 assumed. This file used to return `err.message` for any `ApiError`,
 * on the reasoning that it was "the server's own sentence, chosen by the backend for this exact
 * failure". That is true of the controllers this product wrote and false of everything underneath
 * them: `ApiError.message` is whatever is in the response envelope, and for the users-permissions
 * plugin that is its own English default. `"Email is already taken"` reached a brand-new Thai
 * account holder on `InviteRedeemScreen`, signed out, with nobody beside them — and the i18n gates
 * could not see it, because the string arrives at runtime.
 *
 * So a server sentence is now shown only where this app can name it: `err.code` is matched against
 * the codes this product's own controllers send (`strapi-template/src/api/**`), and the match
 * resolves through the dictionary like every other sentence in the UI. Anything else — an unknown
 * code, a plugin default, a `TypeError` from a dropped fetch, a `DOMException` from the media
 * element, a rejected non-`Error` value — falls back to the caller's own translated sentence.
 *
 * `fallback` is a parameter rather than a `t()` call made in here because this is a plain function
 * with no component around it: the caller, which has `t()`, supplies the sentence. `t` itself is
 * optional for the same reason it is passed at all — a caller with no translator still gets its
 * fallback, never English from the wire.
 */

/**
 * Codes this product's own backend sends, and the sentence the UI says for each. Deliberately NOT
 * every code in `strapi-template` — the ingest pipeline's own codes have a richer home already
 * (`features/admin/ingestQueue.ts`'s `describeIngestFailure`, which also classifies by `status`),
 * and a code that never reaches these screens would be untested copy.
 *
 * A code missing from here is not a bug: it degrades to the caller's fallback, which is always a
 * real sentence. Adding one is a dictionary entry and a line here.
 */
export const API_ERROR_KEY_BY_CODE: Record<string, MessageKey> = {
  // MVP-390: not the backend's codes — `api/client.ts` raises these two itself when `fetch` never
  // reached a server at all. They used to travel as English sentences written beside the code, and
  // the sentence was what got rendered. Both take `{host}`, which rides in `ApiError.details`.
  OFFLINE: 'server.offline',
  SERVER_UNREACHABLE: 'server.unreachable',
  AUTH_REQUIRED: 'server.authRequired',
  // MVP-645: the account-window refusals (`utils/account-window.js`, sent by `auth-guard.js` as a
  // 403 AFTER the password has already been checked and found correct). Two codes because the fix
  // differs in kind — one account is early, the other is over — and both are permanent conditions
  // that only whoever administers the account can clear. Unmapped they reached `LoginDialog`'s
  // network arm and read as "could not reach the server… try again", which is an instruction to
  // repeat the one action that can never work.
  ACCOUNT_NOT_YET_VALID: 'server.accountNotYetValid',
  ACCOUNT_EXPIRED: 'server.accountExpired',
  RATE_LIMITED: 'server.rateLimited',
  SESSION_NOT_FOUND: 'server.sessionNotFound',
  // MVP-567: every rotation action resolves the room's active night first (`getActiveSession`), and
  // since MVP-563 `song-request`'s accept does too. Both refuse with this code, and neither had a
  // sentence — so the one condition the KJ can fix themselves, in one tap, read as a generic 409.
  // Distinct from `SESSION_NOT_FOUND`, which is a night that WAS live and is not anymore.
  NO_ACTIVE_SESSION: 'server.noActiveSession',
  // MVP-646 (a): `session.create`'s two closed-door refusals. The backend spends two codes on one
  // condition on purpose (`session.js:204-206`) — an operator looking at an open room on their own
  // screen has to be told it is the VENUE that is deactivated, or they spend the evening toggling
  // the wrong checkbox — and the console collapsed both into `ops.session.startFailedFallback`,
  // erasing exactly the distinction the two codes were paid for. Two entries, two sentences.
  ROOM_CLOSED: 'server.roomClosed',
  VENUE_CLOSED: 'server.venueClosed',
  NOTHING_TO_UNDO: 'server.nothingToUndo',
  NOTHING_TO_REDO: 'server.nothingToRedo',
  NOTHING_TO_MERGE: 'server.nothingToMerge',
  PATCH_EMPTY: 'server.patchEmpty',
  DATE_INVALID: 'server.dateInvalid',
  PHASE_INVALID: 'server.phaseInvalid',
  REQUEST_ALREADY_DECIDED: 'server.requestAlreadyDecided',
  REQUEST_NOT_RESTORABLE: 'server.requestNotRestorable',
  REQUEST_TOO_OLD_TO_RESTORE: 'server.requestTooOldToRestore',
  // MVP-646 (b): the undo-accept path's fourth refusal, and the only one of the four with no branch
  // in `OperatorConsole` beside its siblings. It matters more than they do: the queue-entry removal
  // failed, so the controller deliberately LEAVES the row in the queue and the song is still going
  // to play — told only "request failed", the KJ does nothing and finds out from the Stage. The
  // sentence carries the workaround the server's own text carries, which is the queue column.
  QUEUE_ENTRY_NOT_REMOVABLE: 'server.queueEntryNotRemovable',
  // MVP-648 (b): rotation and queue reorder/rename against a row a second operator deleted between
  // this screen's last render and the drop. Two codes, one sentence: `rotation.js` says slot and
  // `queue.js` says entry, but to the KJ both mean "the line you dragged is not there any more",
  // and both panels refetch straight afterwards, so the words only have to explain the refetch.
  SLOT_NOT_FOUND: 'server.queueRowGone',
  ENTRY_NOT_FOUND: 'server.queueRowGone',
  REQUESTS_CLOSED: 'server.requestsClosed',
  REQUESTS_FULL: 'server.requestsFull',
  SONG_ALREADY_DELETED: 'server.songAlreadyDeleted',
  SONG_NOT_DELETED: 'server.songNotDeleted',
  SONG_REQUIRED: 'server.songRequired',
  INVITE_INVALID: 'server.inviteInvalid',
  INVITE_EXPIRED: 'server.inviteExpired',
  // MVP-644: the refusal MVP-510 slice 1 added to `invite.redeem`. An invite issued before that
  // deploy carries no venue, and minting the account anyway would land it on the default shop — the
  // leak that slice closed — so redemption refuses instead. Unmapped it fell through to the redeem
  // screen's generic fallback, which told a brand-new account holder nothing about what to do.
  INVITE_NO_VENUE: 'server.inviteNoVenue',
  USER_EXISTS: 'server.userExists',
  USERNAME_INVALID: 'server.usernameInvalid',
  EMAIL_INVALID: 'server.emailInvalid',
  PASSWORD_WEAK: 'server.passwordWeak',
  ROLE_MISSING: 'server.roleMissing',
  ROLE_INVALID: 'server.roleInvalid',
  SELF_LOCKOUT: 'server.selfLockout',
  // MVP-508: the two refusals `POST /api/rooms` answers that the Rooms screen can name. The cap one
  // matters most — the server's own sentence is English and quotes `maxRoomsPerVenue:` at a venue
  // owner, and this screen's whole reason for existing is that the cap should read as a sentence.
  ROOM_CAP_REACHED: 'server.roomCapReached',
  VENUE_NOT_IN_SCOPE: 'server.venueNotInScope',
  // MVP-655: a `shop` invite that named no branch. The form disables its own Create button until a
  // branch is chosen, so this is the backstop rather than the everyday path — but it is the one
  // refusal on that form whose remedy is a field the admin can see, and "could not save that
  // change" would send them looking at the role instead.
  VENUE_REQUIRED: 'server.venueRequired',
  // MVP-648 (a): `nextRoomSlug` found no free slug under this venue's prefix. The only way out is
  // in the server's own sentence — rename an existing room — and generically ("could not save that
  // change") the Add room button reads as broken, so the operator retypes the same name and gets
  // the same refusal. The slug is derived from the venue, never typed, so it is not named here.
  ROOM_SLUG_UNAVAILABLE: 'server.roomSlugUnavailable',
  // MVP-635: the second half of the refusal MVP-615's follow-up split out of `VENUE_NOT_IN_SCOPE`
  // (`refuseOperatorRoom`, `utils/venue-scope.js`) — the operator's venue exists and reaches them,
  // it simply has no room row, so six playback calls refuse. Unmapped it degraded to the caller's
  // generic fallback ("could not save that change"), which names neither the condition nor the one
  // person who can clear it. The server's own sentence is not an option either: it is English and
  // ends in `POST /api/rooms`, in front of a KJ. Reachable today only through a venue made in the
  // Strapi admin panel, which bypasses `venue.create` and so gets no default room.
  VENUE_HAS_NO_ROOM: 'server.venueHasNoRoom',
  // MVP-510 slice 3: the venue-lifetime gate. A staff account whose branch was marked as no longer
  // trading is refused at the login box (and on every request, where the app sees a plain 401).
  // Mapped here because the login screen is the one place a person can read it, and the server's own
  // sentence is English in front of a KJ.
  VENUE_NOT_TRADING: 'server.venueNotTrading',
  // MVP-636: the half-done create. `venue.create` makes the venue, then opens its default room, and
  // both throw sites for this 500 mean the same thing to the reader — the branch is on the list, it
  // has no room, and nobody can play in it until one exists. Unmapped it degraded to the Licences
  // screen's `licences.saveError` ("could not save that change"), which reads as "nothing happened"
  // over a venue that DID get created. Nothing is interpolated: since 0323f37 the 500 body points at
  // the server log rather than carrying the driver's text, so there is no cause to pass through here
  // and no table name to put in front of an operator.
  VENUE_ROOM_NOT_CREATED: 'server.venueRoomNotCreated',
  // MVP-509: the two refusals the VENUE controller answers, which the Licences screen can now
  // reach. Neither had a Thai sentence before this task. `VENUE_CAP_REACHED` matters for the same
  // reason `ROOM_CAP_REACHED` does one level down — the server's own text is English and quotes
  // `maxVenues:` at the reader — and it carries the same warning, that the count includes branches
  // with `isActive: false`, so deactivating one frees nothing.
  VENUE_CAP_REACHED: 'server.venueCapReached',
  VENUE_ADMIN_ONLY: 'server.venueAdminOnly',
  // MVP-641: MVP-620's create guard (`refuseNoLicence`, 400 not 403 — the caller is an admin and it
  // is the body that is wrong). The backend writes two sentences behind this one code, for "named no
  // licence" and "named a licence that does not exist", but sends no discriminator: `details` is
  // `{ code }` alone, so this map — which keys on the code — cannot tell them apart, and one
  // sentence covers both. Both are fixed the same way on the Licences form anyway.
  VENUE_HAS_NO_LICENCE: 'server.venueHasNoLicence',
  // MVP-570: the room-count route's refusal. The Licences screen swallows this one (the count is the
  // only read it tolerates failing), so the sentence is here for every OTHER caller — a `shop`
  // account that reached the route by hand, and anything built on this module later.
  VENUE_COUNTS_ADMIN_ONLY: 'server.venueCountsAdminOnly',
  // MVP-278: the self-serve sign-up (`plan.signup`) and the slip payment (`payment.*`). Every one of
  // them is shown to a shop owner or an admin on a screen built for it, and the server's sentences
  // are English.
  SHOP_NAME_INVALID: 'server.shopNameInvalid',
  PHONE_INVALID: 'server.phoneInvalid',
  TRIAL_ALREADY_USED: 'server.trialAlreadyUsed',
  TRIAL_UNAVAILABLE: 'server.trialUnavailable',
  LICENCE_REQUIRED: 'server.licenceRequired',
  PLAN_NOT_FOUND: 'server.planNotFound',
  PLAN_NOT_PURCHASABLE: 'server.planNotPurchasable',
  PLAN_NOT_PRICED: 'server.planNotPriced',
  SLIP_REQUIRED: 'server.slipRequired',
  SLIP_INVALID: 'server.slipInvalid',
  // MVP-278 harden.
  SLIP_TOO_LARGE: 'server.slipTooLarge',
  SLIP_DUPLICATE: 'server.slipDuplicate',
  LICENCE_NOT_SELF_SERVE: 'server.licenceNotSelfServe',
  SIGNUP_CLOSED: 'server.signupClosed',
  ADMIN_ONLY: 'server.adminOnly',
  PAYMENT_INCOMPLETE: 'server.paymentIncomplete',
  PAYMENT_ALREADY_DECIDED: 'server.paymentAlreadyDecided',
  NOTE_REQUIRED: 'server.noteRequired',
  // MVP-278 email verification.
  EMAIL_NOT_CONFIRMED: 'server.emailNotConfirmed',
  EMAIL_SEND_FAILED: 'server.emailSendFailed',
  SIGNUP_PENDING: 'server.signupPending',
  CONFIRM_INVALID: 'server.confirmInvalid',
  CONFIRM_EXPIRED: 'server.confirmExpired',
  ORIGIN_UNKNOWN: 'server.originUnknown',
  // MVP-278: the payment account in Settings.
  BANK_ACCOUNT_INCOMPLETE: 'server.bankAccountIncomplete',
  BANK_ACCOUNT_NO_INVALID: 'server.bankAccountNoInvalid',
  PROMPTPAY_ID_INVALID: 'server.promptPayIdInvalid',
};

/** The dictionary key for a caught value, or null when this app cannot name what went wrong. */
export function apiErrorKey(err: unknown): MessageKey | null {
  if (!(err instanceof ApiError) || !err.code) return null;
  return API_ERROR_KEY_BY_CODE[err.code] ?? null;
}

export function apiErrorMessage(err: unknown, fallback: string, t?: TFunction): string {
  const key = apiErrorKey(err);
  if (!key || !t) return fallback;
  // MVP-390: `details` is the error's own interpolation values — the failing host, for the two
  // network codes. Passing it for every code is deliberate: a sentence with no slots ignores extra
  // params, so a future code that needs one does not have to come back and re-plumb this line.
  const params = err instanceof ApiError ? err.details : undefined;
  return t(key, params as Record<string, string | number> | undefined);
}
